import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { WishListItem } from '../../types/wishlist';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface WishListFormProps {
  isOpen: boolean;
  onClose: () => void;
  onItemCreated: () => void;
  initialItem?: WishListItem | null;
  isEditMode?: boolean;
}

const initialFormData: Omit<WishListItem, 'id' | 'created_at' | 'updated_at'> = {
  title: '',
  cost: 0,
  comment: '',
  priority: 'medium',
  category_id: undefined,
  is_purchased: false,
  url: '',
};

const WishListForm: React.FC<WishListFormProps> = ({
  isOpen,
  onClose,
  onItemCreated,
  initialItem = null,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);

  useEffect(() => {
    // Fetch categories for dropdown
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get('/api/categories/');
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialItem) {
        setFormData({
          title: initialItem.title,
          cost: initialItem.cost,
          comment: initialItem.comment || '',
          priority: initialItem.priority,
          category_id: initialItem.category?.id,
          is_purchased: initialItem.is_purchased,
          url: initialItem.url || '',
        });
      } else {
        setFormData(initialFormData);
      }
    }
    if (!isOpen) {
      setTimeout(() => {
        setSubmitError(null);
        setSubmitSuccess(false);
      }, 300);
    }
  }, [isOpen, isEditMode, initialItem]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 :
              type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              value
    }));
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      if (isEditMode) {
        const response = await apiClient.put(`/api/wishlist/${initialItem?.id}/`, formData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onItemCreated();
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to update wish list item (${response.status})`);
        }
      } else {
        const response = await apiClient.post('/api/wishlist/', formData);
        if (response.status >= 200 && response.status < 300) {
          setSubmitSuccess(true);
          onItemCreated();
          setFormData(initialFormData);
          setTimeout(() => { onClose(); }, 1500);
        } else {
          throw new Error(`Failed to create wish list item (${response.status})`);
        }
      }
    } catch (err: unknown) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} wish list item:`, err);
      const errorMsg = err instanceof Error ? err.message :
        `Failed to ${isEditMode ? 'update' : 'create'} wish list item.`;
      setSubmitError(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    marginTop: SPACING[4],
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    color: COLORS.text.primary,
    backgroundColor: COLORS.bg.panel,
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{ position: 'fixed', inset: 0, overflowY: 'auto' }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all 0.2s',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size.lg,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Wish List Item' : 'Add to Wish List'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: COLORS.text.tertiary,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: SPACING[4],
                    }}
                    onClick={onClose}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                  {/* Title */}
                  <div>
                    <label htmlFor="title" style={labelStyle}>
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      required
                      style={inputStyle}
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., MacBook Pro, Standing Desk"
                    />
                  </div>

                  {/* Cost */}
                  <div>
                    <label htmlFor="cost" style={labelStyle}>
                      Cost *
                    </label>
                    <input
                      type="number"
                      id="cost"
                      name="cost"
                      step="0.01"
                      min="0"
                      required
                      style={inputStyle}
                      value={formData.cost}
                      onChange={handleInputChange}
                      placeholder="0.00"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" style={labelStyle}>
                      Priority
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      style={inputStyle}
                      value={formData.priority}
                      onChange={handleInputChange}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category_id" style={labelStyle}>
                      Category
                    </label>
                    <select
                      id="category_id"
                      name="category_id"
                      style={inputStyle}
                      value={formData.category_id || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">No Category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* URL */}
                  <div>
                    <label htmlFor="url" style={labelStyle}>
                      URL
                    </label>
                    <input
                      type="url"
                      id="url"
                      name="url"
                      style={inputStyle}
                      value={formData.url}
                      onChange={handleInputChange}
                      placeholder="https://..."
                    />
                  </div>

                  {/* Comment */}
                  <div>
                    <label htmlFor="comment" style={labelStyle}>
                      Notes
                    </label>
                    <textarea
                      id="comment"
                      name="comment"
                      rows={3}
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                      }}
                      value={formData.comment}
                      onChange={handleInputChange}
                      placeholder="Any additional notes or details..."
                    />
                  </div>

                  {/* Purchase Status (only in edit mode) */}
                  {isEditMode && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id="is_purchased"
                        name="is_purchased"
                        style={{
                          height: '16px',
                          width: '16px',
                          accentColor: COLORS.accent.default,
                          borderRadius: RADIUS,
                          border: `1px solid ${COLORS.border.default}`,
                          cursor: 'pointer',
                        }}
                        checked={formData.is_purchased}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="is_purchased" style={{
                        marginLeft: SPACING[8],
                        display: 'block',
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.primary,
                      }}>
                        Mark as purchased
                      </label>
                    </div>
                  )}

                  {/* Error/Success Messages */}
                  {submitError && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[8],
                      color: COLORS.status.error,
                    }}>
                      <ExclamationCircleIcon style={{ height: '20px', width: '20px' }} />
                      <span style={{ fontSize: TYPOGRAPHY.size.sm }}>{submitError}</span>
                    </div>
                  )}

                  {submitSuccess && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[8],
                      color: COLORS.status.success,
                    }}>
                      <CheckCircleIcon style={{ height: '20px', width: '20px' }} />
                      <span style={{ fontSize: TYPOGRAPHY.size.sm }}>
                        {isEditMode ? 'Item updated successfully!' : 'Item added to wish list!'}
                      </span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div style={{ marginTop: SPACING[24] }}>
                    <button
                      type="submit"
                      disabled={isSubmitting || submitSuccess}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: `${SPACING[8]} ${SPACING[16]}`,
                        border: 'none',
                        borderRadius: RADIUS,
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.bg.panel,
                        backgroundColor: (isSubmitting || submitSuccess) ? COLORS.text.tertiary : COLORS.accent.default,
                        cursor: (isSubmitting || submitSuccess) ? 'not-allowed' : 'pointer',
                        opacity: (isSubmitting || submitSuccess) ? 0.5 : 1,
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <ArrowPathIcon style={{
                            height: '16px',
                            width: '16px',
                            marginRight: SPACING[8],
                            animation: 'spin 1s linear infinite',
                          }} />
                          {isEditMode ? 'Updating...' : 'Adding...'}
                        </>
                      ) : submitSuccess ? (
                        <>
                          <CheckCircleIcon style={{ height: '16px', width: '16px', marginRight: SPACING[8] }} />
                          Success!
                        </>
                      ) : (
                        isEditMode ? 'Update Item' : 'Add to Wish List'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WishListForm;
